import { useCallback, useMemo, useRef, useState } from 'react'

const ZOOM_STEPS = [0.5, 0.75, 0.9, 1, 1.25, 1.5, 2]

export function useScaling({ initialFitToWidth }: { initialFitToWidth?: boolean }) {
  const [fitToWidth, setFitToWidth] = useState(initialFitToWidth ?? false)
  const [scaleFactor, setScaleFactor] = useState(1)
  const scrollInfoRef = useRef<{
    ratioY: number
    ratioX: number
  } | null>(null)

  const zoomOutDisabled = useMemo(() => {
    return scaleFactor === ZOOM_STEPS[0] && !fitToWidth
  }, [scaleFactor, fitToWidth])

  const zoomInDisabled = useMemo(() => {
    return scaleFactor === ZOOM_STEPS[ZOOM_STEPS.length - 1]
  }, [scaleFactor])

  const captureScrollPosition = useCallback((scrollableRef: React.RefObject<HTMLDivElement>) => {
    if (!scrollableRef.current) return

    const container = scrollableRef.current
    const scrollTop = container.scrollTop
    const scrollLeft = container.scrollLeft
    const scrollHeight = container.scrollHeight
    const scrollWidth = container.scrollWidth
    const clientHeight = container.clientHeight
    const clientWidth = container.clientWidth

    // Calculate center point ratios for both dimensions
    const visibleCenterY = scrollTop + clientHeight / 2
    const visibleCenterX = scrollLeft + clientWidth / 2
    const centerRatioY = scrollHeight > 0 ? visibleCenterY / scrollHeight : 0
    const centerRatioX = scrollWidth > 0 ? visibleCenterX / scrollWidth : 0

    scrollInfoRef.current = {
      ratioY: centerRatioY,
      ratioX: centerRatioX,
    }
  }, [])

  const onFitToWidth = useCallback(
    (scrollableRef?: React.RefObject<HTMLDivElement>) => {
      if (scrollableRef) captureScrollPosition(scrollableRef)
      if (scaleFactor !== 1) {
        setScaleFactor(1)
        setFitToWidth(true)
        return
      }
      setFitToWidth(!fitToWidth)
    },
    [captureScrollPosition, fitToWidth, scaleFactor],
  )

  const onZoomIn = useCallback(
    (scrollableRef?: React.RefObject<HTMLDivElement>) => {
      if (scrollableRef) captureScrollPosition(scrollableRef)
      if (scaleFactor === 1 && !fitToWidth) {
        setFitToWidth(true)
        return
      }
      const currentZoomIndex = ZOOM_STEPS.indexOf(scaleFactor)
      if (currentZoomIndex === -1 || currentZoomIndex === ZOOM_STEPS.length - 1) return
      setScaleFactor(ZOOM_STEPS[currentZoomIndex + 1])
    },
    [captureScrollPosition, fitToWidth, scaleFactor],
  )

  const onZoomOut = useCallback(
    (scrollableRef?: React.RefObject<HTMLDivElement>) => {
      if (scrollableRef) captureScrollPosition(scrollableRef)
      if (scaleFactor === 1 && fitToWidth) {
        setFitToWidth(false)
        return
      }
      const currentZoomIndex = ZOOM_STEPS.indexOf(scaleFactor)
      if (currentZoomIndex === -1 || currentZoomIndex === 0) return
      setScaleFactor(ZOOM_STEPS[currentZoomIndex - 1])
    },
    [captureScrollPosition, fitToWidth, scaleFactor],
  )

  return {
    fitToWidth,
    setFitToWidth,
    scaleFactor,
    setScaleFactor,
    onFitToWidth,
    onZoomIn,
    onZoomOut,
    zoomOutDisabled,
    zoomInDisabled,
    scrollInfoRef,
  }
}
